import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ClientTestimonial from '../components/ClientTestimonial';
import SEO from '../components/SEO';
import { DiagnosticsInstruments } from '../components/DiagnosticsInstruments';
import { ArrowRight, Target, Lightbulb, FileText, Users, BarChart3, CheckCircle, Layers } from 'lucide-react';

const impactASPlusLogo = "/opt/brand/impactas-final-2.webp";

const diagnosticPrinciples = [
  "Illuminate what matters most",
  "Connect behaviour to outcomes",
  "Support prioritisation, not noise",
  "Lead to informed action"
];

const ourApproach = [
  "Select diagnostics based on purpose, population, and context",
  "Integrate insight across individual, team, and organisational levels",
  "Interpret results through an African and organisational lens",
  "Anchor findings in practical leadership decisions"
];

const reportBenefits = [
  "Bring together insight across levels",
  "Highlight strengths, risks, and priority areas",
  "Support leadership development, succession, coaching, and culture work",
  "Enable consistent interpretation across cohorts and over time"
];

const externalTools = [
  "Hogan personality and risk assessments",
  "Everything DISC® profiles and team diagnostics",
  "Leadership Circle Profile™"
];

const commonApplications = [
  "360° feedback and leadership assessment programmes",
  "Leadership development and succession planning",
  "Coaching and behaviour change initiatives",
  "Team effectiveness and alignment",
  "Culture, ethics, and behavioural risk reviews",
  "Hiring and assessment centre decisions"
];

const insightToAction = [
  "Advisory guidance",
  "Leadership development",
  "Coaching and facilitation",
  "Organisational interventions"
];

const DiagnosticsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Leadership Assessment & 360 Feedback Nigeria | RESILIGENT Lagos"
        description="Leadership diagnostics for Nigerian organisations: twelve Impact AS Plus™ instruments, from graduate intakes to senior appointments."
        keywords="360 feedback Nigeria, leadership assessment Lagos, psychometric testing Nigeria, leadership diagnostics Africa, 360 degree feedback Lagos, executive assessment Nigeria, personality assessment Lagos, leadership evaluation Nigeria, talent assessment Africa"
        canonical="/diagnostics"
      />
      <Header />
      
      <main>
        {/* Hero Section with Background Image */}
        <section 
          className="relative min-h-[70vh] lg:min-h-[75vh] flex items-center bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(10, 10, 10, 0.95) 0%, rgba(10, 10, 10, 0.8) 40%, rgba(10, 10, 10, 0.4) 100%), url('/opt/photo/ext/u1551288049-bebda4e38f71.webp')`
          }}
        >
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-24">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-px bg-[#2693d0]" />
                <p className="text-[#2693d0] text-sm font-medium tracking-wide uppercase">
                  Powered by Impact AS Plus™
                </p>
              </div>
              
              <h1 className="text-white font-semibold text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight mb-8">
                Diagnostics & Insights
              </h1>
              
              <p className="text-[#2693d0] text-lg lg:text-xl font-medium leading-relaxed mb-6">
                Insight that informs decisions, not data for its own sake
              </p>
              
              <p className="text-slate-300 text-base lg:text-lg leading-relaxed mb-6">
                RESILIGENT uses diagnostics and insight reports to support confident leadership and organisational decisions, not to overwhelm organisations with disconnected data.
              </p>
              
              <p className="text-slate-300 text-base lg:text-lg xl:text-xl leading-relaxed max-w-2xl">
                All diagnostics are interpreted through Impact AS Plus™, our proprietary leadership and organisational intelligence system, ensuring insight is coherent, contextual, and actionable.
              </p>
            </div>
          </div>
        </section>

        {/* Why Diagnostics Matter */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  Why Diagnostics Matter: When Used Properly
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  Leadership challenges are rarely caused by a lack of information.
                </p>
                <p className="text-slate-800 font-medium text-lg lg:text-xl leading-relaxed">
                  They arise when insight is fragmented, misinterpreted, or disconnected from decision-making.
                </p>
              </div>
              
              <div className="bg-[#f0f5f8] rounded-xl p-8 border border-slate-100">
                <p className="text-slate-700 font-medium text-lg mb-6">Our approach ensures that diagnostics:</p>
                <ul className="space-y-4">
                  {diagnosticPrinciples.map((item, index) => (
                    <li key={index} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                      </div>
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Our Diagnostic Philosophy */}
        <section className="relative bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                Our Diagnostic Philosophy
              </h2>
              
              <p className="text-slate-800 font-medium text-lg lg:text-xl leading-relaxed mb-8">
                We do not believe in one-size-fits-all assessments.
              </p>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                Instead, we:
              </p>
              
              <ul className="space-y-4 mb-10">
                {ourApproach.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="bg-white rounded-xl p-6 card-pulse-border">
                <p className="text-slate-800 font-semibold text-lg lg:text-xl text-center">
                  Diagnostics are a means, not the end.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* RESILIGENT Proprietary Diagnostics - NEW SECTION */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  RESILIGENT proprietary diagnostics
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  RESILIGENT has developed a set of proprietary diagnostics designed to surface leadership behaviour, decision patterns, and organisational dynamics in African and complex operating contexts.
                </p>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  These tools are purpose-built to reflect:
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">local organisational realities</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">leadership and cultural nuance</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">the intersection of behaviour, risk, and execution</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  The full suite spans people, teams, and organisations, and is set out below.
                </p>
                <div className="bg-[#2693d0]/5 rounded-xl p-5 border border-[#2693d0]/20">
                  <p className="text-slate-700 text-base leading-relaxed">
                    These diagnostics are native to Impact AS Plus™ and form the primary source of insight in many engagements.
                  </p>
                </div>
              </div>
            </div>

            {/* The twelve instruments, grouped */}
            <DiagnosticsInstruments className="mt-16" />
            
            {/* Client Testimonial - Sandra Okoli */}
            <div className="mt-16 max-w-3xl mx-auto">
              <ClientTestimonial
                quote="The self-awareness created through the leadership assessment was a pivotal moment for me. It helped me clarify my aspirations for the next five, ten, and fifteen years, and gave me a language to think more deliberately about my leadership trajectory."
                name="Sandra Okoli"
                title="Deputy General Manager"
                company="Polaris Bank"
                variant="light"
              />
            </div>
          </div>
        </section>

        {/* Impact AS Plus Insight Reports */}
        <section className="bg-[#1e3a5f] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <img 
                  src={impactASPlusLogo} 
                  alt="Impact AS Plus" 
                  className="h-12 lg:h-14 w-auto mb-8"
                />
                <h2 className="text-white font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                  Impact AS Plus™ Insight Reports
                </h2>
                <p className="text-slate-200 text-base lg:text-lg leading-relaxed mb-6">
                  At the core of our work are Impact AS Plus™ Insight Reports, which synthesise diagnostic data into a unified leadership and organisational view.
                </p>
                <p className="text-slate-300 text-base lg:text-lg leading-relaxed">
                  Impact AS Plus allows organisations to see patterns, not just profiles.
                </p>
              </div>
              
              <div className="bg-white/5 border border-white/10 rounded-xl p-8">
                <p className="text-slate-300 font-medium mb-6">These reports:</p>
                <ul className="space-y-4">
                  {reportBenefits.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                      <span className="text-slate-200 text-base leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Integrating External Diagnostics */}
        <section className="relative bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                Integrating External Diagnostics (When Appropriate)
              </h2>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                Where specific additional perspectives are required, RESILIGENT may integrate external, globally recognised diagnostic tools as complementary inputs into Impact AS Plus™.
              </p>
              
              <p className="text-slate-800 font-medium text-base lg:text-lg leading-relaxed mb-6">
                These tools do not replace RESILIGENT's proprietary diagnostics. They are used selectively to deepen or triangulate insight where appropriate.
              </p>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                These may include, for example:
              </p>
              
              <ul className="space-y-3 mb-8">
                {externalTools.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full flex-shrink-0" />
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              
              <a
                href="/partner-assessments"
                className="inline-flex items-center gap-2 text-[#2693d0] font-medium hover:underline mb-8"
              >
                View all partner assessments
                <ArrowRight size={16} />
              </a>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                Such tools are selected deliberately, not by default, and are always:
              </p>
              
              <div className="grid sm:grid-cols-3 gap-4 mb-8">
                <div className="bg-[#f0f5f8] rounded-lg p-5 text-center">
                  <Lightbulb className="w-6 h-6 text-[#2693d0] mx-auto mb-3" />
                  <p className="text-slate-700 text-sm font-medium">Contextualised within the organisation</p>
                </div>
                <div className="bg-[#f0f5f8] rounded-lg p-5 text-center">
                  <Users className="w-6 h-6 text-[#2693d0] mx-auto mb-3" />
                  <p className="text-slate-700 text-sm font-medium">Interpreted by experienced practitioners</p>
                </div>
                <div className="bg-[#f0f5f8] rounded-lg p-5 text-center">
                  <Layers className="w-6 h-6 text-[#2693d0] mx-auto mb-3" />
                  <p className="text-slate-700 text-sm font-medium">Integrated into a broader intelligence framework</p>
                </div>
              </div>
              
              <div className="bg-[#f0f5f8] rounded-xl p-6 border border-slate-100">
                <p className="text-slate-800 font-medium text-lg lg:text-xl leading-relaxed text-center">
                  The value lies not in the tool itself, but in how insight is interpreted and applied.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Early Career & Graduate Assessment */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Early Career & Graduate Assessment
              </h2>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                Impact AS Plus™ supports graduate recruitment and assessment centres using proprietary diagnostics including <span className="text-[#2693d0] font-medium">JobFit™</span> and <span className="text-[#2693d0] font-medium">InsightEdge™</span>, providing structured insight into role fit, reasoning, and future potential.
              </p>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                JobFit™ is also used for experienced and senior hires, with JobFit Plus™ for senior appointments.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <a
                  href="/early-career-selection"
                  className="inline-flex items-center gap-2 text-[#2693d0] font-medium hover:underline"
                >
                  Learn more about Career Selection & Assessment Centres
                  <ArrowRight size={16} />
                </a>
                <a
                  href="/leadership-succession-intelligence"
                  className="inline-flex items-center gap-2 text-[#2693d0] font-medium hover:underline"
                >
                  Explore Leadership & Succession Intelligence
                  <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Common Diagnostic Applications */}
        <section className="relative bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  Common Diagnostic Applications
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  Organisations typically use diagnostics and insight reports to support:
                </p>
              </div>
              
              <div>
                <ul className="space-y-4 mb-8">
                  {commonApplications.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  Most organisations begin with a focused application and expand as confidence and clarity increase.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* From Insight to Action */}
        <section className="bg-white py-20 lg:py-28 border-t border-slate-100">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                From Insight to Action
              </h2>
              
              <p className="text-slate-800 font-medium text-lg lg:text-xl leading-relaxed mb-8">
                Diagnostics alone do not change behaviour.
              </p>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                RESILIGENT combines insight reports with:
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {insightToAction.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-[#f0f5f8] rounded-lg p-4">
                    <div className="w-2 h-2 bg-[#2693d0] rounded-full flex-shrink-0" />
                    <span className="text-slate-700 text-base font-medium">{item}</span>
                  </div>
                ))}
              </div>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                This ensures that insight translates into meaningful and sustained impact.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-[#e8f2f7] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Next steps
              </h2>
              
              {/* Primary CTA */}
              <div className="bg-white card-pulse-border rounded-xl p-8 mb-6 shadow-sm">
                <h3 className="text-slate-800 font-semibold text-xl mb-4">
                  Book a leadership briefing
                </h3>
                <p className="text-slate-600 text-base leading-relaxed mb-6">
                  A guided discussion to determine which diagnostics and insight approach best suit your organisational context.
                </p>
                <a
                  href="/briefing"
                  className="inline-flex items-center gap-2 bg-[#2693d0] text-white px-6 py-3.5 rounded font-medium text-base hover:bg-[#1e7ab8] transition-all duration-300"
                  data-testid="book-briefing-btn"
                >
                  Book a leadership briefing
                  <ArrowRight size={18} />
                </a>
              </div>

              {/* Secondary CTA */}
              <a
                href="/sample-reports"
                className="inline-flex items-center gap-3 text-slate-600 hover:text-[#2693d0] transition-colors duration-300"
                data-testid="view-sample-reports-link"
              >
                <FileText className="w-5 h-5" />
                <span className="text-base font-medium">View sample reports</span>
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </section>

        {/* Footer Note */}
        <section className="bg-[#e8f2f7] pb-12 border-t border-slate-200">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-8">
            <p className="text-slate-500 text-sm">
              All diagnostics are governed by appropriate ethical, professional, and data standards.
            </p>
          </div>
        </section>
      </main>

      <Footer hidePreFooterCta={true} />
    </div>
  );
};

export default DiagnosticsPage;
